// Shared date rendering.
//
// The backend serializes datetimes as naive ISO strings without a timezone
// designator ("2026-09-25T12:00:00.123"), and every stored value is UTC. Per
// ES2015+, `new Date("2026-09-25T12:00:00")` (no offset) is parsed as LOCAL
// time, so a naive UTC string read that way shows 2 h early in summer /
// 1 h in winter — the carrier conversation did exactly that while the ticket
// conversation appended 'Z' (KI-110). Every timestamp goes through here.

import i18next from 'i18next';

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// A Date from a backend timestamp: naive strings are UTC. Returns null when
// the value is empty or unparseable.
export function parseUtc(value) {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
        const fromNumber = new Date(value);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }
    let text = String(value).trim();
    if (!text) return null;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)) text = text.replace(' ', 'T');
    // Microseconds (Python) or 7 digits (Microsoft Graph): keep 3 so every engine parses them.
    text = text.replace(/(T\d{2}:\d{2}:\d{2}\.\d{3})\d+/, '$1');
    if (/^\d{4}-\d{2}-\d{2}T/.test(text) && !HAS_OFFSET.test(text)) text = `${text}Z`;
    if (DATE_ONLY.test(text)) text = `${text}T00:00:00Z`;
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// ---- The one display format (UI rework, 28/10) ------------------------------
//
// Every date the app shows has one short shape, in the operators' timezone
// (all tenants work in France: Europe/Paris), French first:
//   date + time  « 28 oct., 14:05 »   another year « 28 oct. 2025, 14:05 »
//   date only    « 28 oct. »          another year « 28 oct. 2025 »
//   time only    « 14:05 »            (timelines, same-day context)
//   relative     « à l’instant », « il y a 12 min », « il y a 14 h », « hier »,
//                « il y a 3 j », then the short date (recency: last sync,
//                last activity — put formatShortDateTime in the title).
// English through the same helpers: « Oct 28, 14:05 », « Oct 28, 2025, 14:05 ».
// `lang` is an i18n language (« fr », « fr-FR », « en »…); left out, the
// interface language (French until i18n is ready, and in unit tests). Empty or
// unreadable values give « — » (or `empty`). `now` is injectable for tests.

const PARIS = 'Europe/Paris';

const langOf = (lang) => String(
    lang || (i18next.isInitialized && (i18next.resolvedLanguage || i18next.language)) || 'fr',
).toLowerCase();

// i18n language → the locale the dates are written in.
export function displayLocale(lang) {
    const code = langOf(lang);
    if (code.startsWith('en')) return 'en-US';
    if (code.startsWith('es')) return 'es-ES';
    if (code.startsWith('zh')) return 'zh-CN';
    return 'fr-FR';
}

const asDate = (now) => (now instanceof Date ? now : new Date(now === undefined || now === null ? Date.now() : now));

// Calendar parts of an instant in a timezone.
function dayParts(date, timeZone) {
    const parts = {};
    new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' })
        .formatToParts(date)
        .forEach(({ type, value }) => { parts[type] = Number(value); });
    return parts;
}

const dayNumber = (date, timeZone) => {
    const { year, month, day } = dayParts(date, timeZone);
    return Math.round(Date.UTC(year, month - 1, day) / 86400000);
};

// `timeZone` is Paris, except for carrier wall-clock times (rendered in UTC,
// see formatTrackingEventDate); « this year » is always the Paris year of now.
function shortDay(date, lang, timeZone, now) {
    const withYear = dayParts(date, timeZone).year !== dayParts(asDate(now), PARIS).year;
    return date.toLocaleDateString(displayLocale(lang), {
        timeZone, day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}),
    });
}

function shortTime(date, lang, timeZone, seconds) {
    return date.toLocaleTimeString(displayLocale(lang), {
        timeZone, hourCycle: 'h23', hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}),
    });
}

function shortDayTime(date, lang, timeZone, now, seconds) {
    const joiner = displayLocale(lang).startsWith('zh') ? ' ' : ', ';
    return `${shortDay(date, lang, timeZone, now)}${joiner}${shortTime(date, lang, timeZone, seconds)}`;
}

// « 28 oct., 14:05 » / « 28 oct. 2025, 14:05 » — `seconds: true` adds them
// (AI trace spans, call transcripts).
export function formatShortDateTime(value, lang, { now, empty = '—', seconds = false } = {}) {
    const date = parseUtc(value);
    return date ? shortDayTime(date, lang, PARIS, now, seconds) : empty;
}

// « 28 oct. » / « 28 oct. 2025 ». A calendar day (« 2026-10-28 ») stays that day.
export function formatShortDate(value, lang, { now, empty = '—' } = {}) {
    const date = parseUtc(value);
    return date ? shortDay(date, lang, PARIS, now) : empty;
}

// « 14:05 » (« 14:05:09 » with `seconds: true`).
export function formatShortTime(value, lang, { empty = '—', seconds = false } = {}) {
    const date = parseUtc(value);
    return date ? shortTime(date, lang, PARIS, seconds) : empty;
}

// The relative wording, through i18n (namespace datesUi); these defaults are
// what shows before the translations load and in unit tests.
const RELATIVE = {
    fr: { now: 'à l’instant', minutes: 'il y a {{n}} min', hours: 'il y a {{n}} h', yesterday: 'hier', days: 'il y a {{n}} j' },
    en: { now: 'just now', minutes: '{{n}} min ago', hours: '{{n}} h ago', yesterday: 'yesterday', days: '{{n}} days ago' },
};

function relativeText(key, lang, n) {
    const defaults = displayLocale(lang) === 'fr-FR' ? RELATIVE.fr : RELATIVE.en;
    const fallback = defaults[key].replace('{{n}}', n);
    if (!i18next.isInitialized) return fallback;
    const text = i18next.t(`datesUi.relative.${key}`, { lng: langOf(lang).slice(0, 2), n, defaultValue: defaults[key] });
    return typeof text === 'string' && text ? text : fallback;
}

// « à l’instant », « il y a 12 min », « il y a 14 h », « hier », « il y a 3 j »
// (Paris calendar days), then the short date from a week on. A future instant
// (a scheduled run) is the short date + time.
export function formatRelative(value, lang, now = Date.now(), { empty = '—' } = {}) {
    const date = parseUtc(value);
    if (!date) return empty;
    const current = asDate(now);
    const elapsed = current.getTime() - date.getTime();
    if (elapsed < -60000) return shortDayTime(date, lang, PARIS, current, false);
    const minutes = Math.floor(elapsed / 60000);
    if (minutes < 1) return relativeText('now', lang);
    if (minutes < 60) return relativeText('minutes', lang, minutes);
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return relativeText('hours', lang, hours);
    const days = dayNumber(current, PARIS) - dayNumber(date, PARIS);
    if (days <= 1) return relativeText('yesterday', lang);
    if (days < 7) return relativeText('days', lang, days);
    return shortDay(date, lang, PARIS, current);
}

// Deprecated names (pre-rework callers, e.g. code merged from main): they now
// give the short format too. `options` only tells a date-only call apart
// (`hour: undefined`); new code calls the helpers above.
export function formatDateTime(value, options = {}, locale) {
    const dateOnly = Object.prototype.hasOwnProperty.call(options || {}, 'hour') && options.hour === undefined;
    return dateOnly ? formatShortDate(value, locale) : formatShortDateTime(value, locale);
}

export function formatTime(value, locale) {
    return formatShortTime(value, locale);
}

// Carrier tracking events (KI-194). A carrier reports the local wall-clock
// time of an event and `date` keeps it as the source wrote it — Ship24 adds a
// fake "Z", Envoi du Net a fake "+00:00", most sources no offset at all — so
// it is NOT a UTC timestamp. The backend adds `date_utc` (true UTC) when the
// source gave a real offset. Same rule as the backend reader
// (app/utils/tracking_time.py): `date_utc` first; else an explicit non-zero
// offset is honoured; else the wall-clock digits are Paris time, shown as
// written, never shifted.
const ISO_WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;
const FR_WALL_CLOCK = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s*(?:à|a)?\s*(\d{1,2})[:hH](\d{2}))?/;
const REAL_OFFSET = /[+-](\d{2}):?(\d{2})$/;

function wallClockParts(text) {
    let match = ISO_WALL_CLOCK.exec(text);
    if (match) {
        return { year: +match[1], month: +match[2], day: +match[3], hour: match[4], minute: match[5] };
    }
    match = FR_WALL_CLOCK.exec(text);
    if (match) {
        const year = +match[3] < 100 ? 2000 + +match[3] : +match[3];
        return { year, month: +match[2], day: +match[1], hour: match[4], minute: match[5] };
    }
    return null;
}

// In the short display format: « 8 oct., 13:02 », « 1 oct. » for a day without
// a time; an unreadable source date is shown as written.
export function formatTrackingEventDate(event, lang, { now } = {}) {
    if (!event) return '—';
    if (event.date_utc) {
        const instant = parseUtc(event.date_utc);
        if (instant) return shortDayTime(instant, lang, PARIS, now, false);
    }
    const raw = event.date;
    if (raw === null || raw === undefined || raw === '') return '—';
    const text = String(raw).trim();
    const offset = REAL_OFFSET.exec(text);
    if (offset && /T\d{2}:\d{2}/.test(text) && (+offset[1] !== 0 || +offset[2] !== 0)) {
        const instant = parseUtc(text);
        if (instant) return shortDayTime(instant, lang, PARIS, now, false);
    }
    const parts = wallClockParts(text);
    if (!parts) return text;
    const hasTime = parts.hour !== undefined;
    // The wall-clock digits built and rendered in UTC: shown exactly as written.
    const wall = new Date(Date.UTC(parts.year, parts.month - 1, parts.day,
        hasTime ? +parts.hour : 0, hasTime ? +parts.minute : 0));
    if (Number.isNaN(wall.getTime())) return text;
    return hasTime ? shortDayTime(wall, lang, 'UTC', now, false) : shortDay(wall, lang, 'UTC', now);
}
